import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Plus, FileText, Edit, Trash, Sparkles, Eye } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Badge color mappings for template types
const getTypeBadge = (type) => {
  const badges = {
    'freelance': { label: 'Freelance', className: 'bg-purple-100 text-purple-700 border-purple-200' },
    'regie': { label: 'Time & Material', className: 'bg-green-100 text-green-700 border-green-200' },
    'project_based': { label: 'Fixed Price', className: 'bg-blue-100 text-blue-700 border-blue-200' },
    'nda': { label: 'NDA', className: 'bg-red-100 text-red-700 border-red-200' },
    'non_compete': { label: 'Non-Compete', className: 'bg-rose-100 text-rose-700 border-rose-200' },
    'custom': { label: 'Custom', className: 'bg-gray-100 text-gray-700 border-gray-200' }
  };
  const badge = badges[type] || badges['custom'];
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded border ${badge.className}`}>
      {badge.label}
    </span>
  );
};

const AVAILABLE_VARIABLES = [
  { name: 'project_title', label: 'Project Title', category: 'Project' },
  { name: 'resource_name', label: 'Resource Name', category: 'Resource' },
  { name: 'pm_company_name', label: 'Project Manager Company Name', category: 'Project Manager Company' },
  { name: 'pm_company_city', label: 'Project Manager Company City', category: 'Project Manager Company' },
  { name: 'pm_company_country', label: 'Project Manager Company Country', category: 'Project Manager Company' },
  { name: 'rm_company_name', label: 'Resource Manager Company Name', category: 'Resource Manager Company' },
  { name: 'rm_company_city', label: 'Resource Manager Company City', category: 'Resource Manager Company' },
  { name: 'rm_company_country', label: 'Resource Manager Company Country', category: 'Resource Manager Company' },
  { name: 'duration_months', label: 'Duration (Months)', category: 'Terms' },
  { name: 'rate_per_day', label: 'Daily Rate', category: 'Financial' },
  { name: 'rate_currency', label: 'Currency', category: 'Financial' },
  { name: 'collaboration_type', label: 'Collaboration Type', category: 'Terms' },
  { name: 'commitment_percentage', label: 'Commitment %', category: 'Terms' },
  { name: 'deliverables', label: 'Deliverables', category: 'Project' },
  { name: 'payment_terms', label: 'Payment Terms', category: 'Financial' },
  { name: 'payment_schedule', label: 'Payment Schedule', category: 'Financial' },
  { name: 'location_city', label: 'Work Location City', category: 'Location' },
  { name: 'location_country', label: 'Work Location Country', category: 'Location' },
  { name: 'jurisdiction', label: 'Jurisdiction', category: 'Legal' },
  { name: 'start_date', label: 'Start Date', category: 'Terms' },
  { name: 'estimated_end_date', label: 'End Date', category: 'Terms' }
];

const ContractTemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    description: '',
    type: 'custom',
    content_template: '',
    required_fields: []
  });
  
  const createTextareaRef = React.useRef(null);
  const editTextareaRef = React.useRef(null);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/contract-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async () => {
    if (!newTemplate.name || !newTemplate.content_template) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/contract-templates`, newTemplate, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Template created successfully');
      setShowCreateModal(false);
      setNewTemplate({
        name: '',
        description: '',
        type: 'custom',
        content_template: '',
        required_fields: []
      });
      fetchTemplates();
    } catch (error) {
      console.error('Error creating template:', error);
      toast.error('Failed to create template');
    }
  };

  const handleViewTemplate = (template) => {
    setSelectedTemplate(template);
    setShowViewModal(true);
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate({ ...template });
    setShowEditModal(true);
  };

  const handleUpdateTemplate = async () => {
    if (!editingTemplate.name || !editingTemplate.content_template) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/contract-templates/${editingTemplate.id}`, editingTemplate, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Template updated successfully');
      setShowEditModal(false);
      setEditingTemplate(null);
      fetchTemplates();
    } catch (error) {
      console.error('Error updating template:', error);
      toast.error('Failed to update template');
    }
  };

  const insertVariable = (variableName, isEditMode = false) => {
    const textareaRef = isEditMode ? editTextareaRef : createTextareaRef;
    const textarea = textareaRef.current;
    
    if (!textarea) return;

    const variable = `{{${variableName}}}`;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentContent = isEditMode ? editingTemplate.content_template : newTemplate.content_template;
    
    const newContent = 
      currentContent.substring(0, start) +
      variable +
      currentContent.substring(end);
    
    if (isEditMode) {
      setEditingTemplate({ ...editingTemplate, content_template: newContent });
    } else {
      setNewTemplate({ ...newTemplate, content_template: newContent });
    }
    
    // Set cursor position after inserted variable
    setTimeout(() => {
      textarea.focus();
      const newPosition = start + variable.length;
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  };

  const handleGenerateWithAI = async () => {
    if (!newTemplate.name) {
      toast.error('Please enter a template name first');
      return;
    }

    setGeneratingAI(true);
    const token = localStorage.getItem('token');
    
    try {
      const response = await axios.post(
        `${API}/contract-templates/generate-content`,
        {
          title: newTemplate.name,
          description: newTemplate.description || 'A professional contract template'
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setNewTemplate({
        ...newTemplate,
        content_template: response.data.content_template
      });
      
      toast.success('Template content generated successfully!');
    } catch (error) {
      console.error('Error generating content:', error);
      toast.error('Failed to generate content with AI');
    } finally {
      setGeneratingAI(false);
    }
  };

  const predefinedTemplates = templates.filter(t => t.is_predefined);
  const customTemplates = templates.filter(t => !t.is_predefined);

  return (
    <>
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-[#1B3240]">Contract Templates</h1>
          <Button className="btn-snow" onClick={() => setShowCreateModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Create Custom Template
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-8">Loading templates...</div>
        ) : (
          <>
            {/* Predefined Templates */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4">Predefined Templates</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {predefinedTemplates.map((template) => (
                  <Card 
                    key={template.id} 
                    className="hover:shadow-lg transition bg-white"
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <CardTitle className="text-base font-semibold text-gray-900">
                          {template.name}
                        </CardTitle>
                        <FileText className="w-5 h-5 text-gray-300 flex-shrink-0 ml-2" />
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <p className="text-sm text-gray-500 mb-3">{template.description}</p>
                      <div className="flex flex-wrap gap-1.5 mb-4">
                        {getTypeBadge(template.type)}
                        <span className="px-2 py-0.5 text-xs font-medium rounded border bg-sky-100 text-sky-700 border-sky-200">
                          Snow Exchange
                        </span>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-gray-600"
                        onClick={() => handleViewTemplate(template)}
                      >
                        <Eye className="w-4 h-4 mr-1.5" />
                        View
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Custom Templates */}
            <div>
              <h2 className="text-xl font-semibold mb-4">Your Custom Templates</h2>
              {customTemplates.length === 0 ? (
                <Card>
                  <CardContent className="py-8 text-center text-gray-500">
                    No custom templates yet. Create your first template!
                  </CardContent>
                </Card>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {customTemplates.map((template) => (
                    <Card 
                      key={template.id} 
                      className="hover:shadow-lg transition bg-white"
                    >
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between">
                          <CardTitle className="text-base font-semibold text-gray-900">
                            {template.name}
                          </CardTitle>
                          <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                            <Button 
                              size="sm" 
                              variant="ghost"
                              className="h-7 w-7 p-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditTemplate(template);
                              }}
                            >
                              <Edit className="w-4 h-4 text-gray-400" />
                            </Button>
                            <FileText className="w-5 h-5 text-gray-300" />
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <p className="text-sm text-gray-500 mb-3">{template.description}</p>
                        <div className="flex flex-wrap gap-1.5 mb-4">
                          {getTypeBadge(template.type)}
                          <span className="px-2 py-0.5 text-xs font-medium rounded border bg-amber-100 text-amber-700 border-amber-200">
                            Custom
                          </span>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-gray-600"
                          onClick={() => handleViewTemplate(template)}
                        >
                          <Eye className="w-4 h-4 mr-1.5" />
                          View
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Templates Disclaimer */}
            <p className="text-xs text-gray-500 mt-6 text-center">
              Templates are provided for convenience only and must be independently reviewed.
            </p>
          </>
        )}

        {/* Create Template Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-6xl max-h-[90vh] overflow-auto">
              <CardHeader>
                <CardTitle>Create Custom Template</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Template Name *</label>
                  <Input
                    value={newTemplate.name}
                    onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                    placeholder="e.g., My Custom Contract"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <Input
                    value={newTemplate.description}
                    onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })}
                    placeholder="Brief description of this template"
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  {/* Template Content - 2 columns */}
                  <div className="col-span-2">
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium">Template Content *</label>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={handleGenerateWithAI}
                        disabled={generatingAI || !newTemplate.name}
                        className="text-purple-600 border-purple-300 hover:bg-purple-50"
                      >
                        <Sparkles className="w-4 h-4 mr-2" />
                        {generatingAI ? 'Generating...' : 'Generate with AI'}
                      </Button>
                    </div>
                    <Textarea
                      ref={createTextareaRef}
                      value={newTemplate.content_template}
                      onChange={(e) => setNewTemplate({ ...newTemplate, content_template: e.target.value })}
                      rows={16}
                      placeholder="Enter your contract template here, or click 'Generate with AI' to create content automatically."
                      className="font-mono text-sm"
                    />
                  </div>

                  {/* Variables Sidebar - 1 column */}
                  <div className="col-span-1">
                    <label className="block text-sm font-medium mb-1">Available Variables</label>
                    <div className="border rounded-lg p-3 bg-gray-50 max-h-[400px] overflow-y-auto">
                      <p className="text-xs text-gray-600 mb-2">Click to insert:</p>
                      {['Project', 'Resource', 'Project Manager Company', 'Resource Manager Company', 'Terms', 'Financial', 'Location', 'Legal'].map(category => {
                        const categoryVars = AVAILABLE_VARIABLES.filter(v => v.category === category);
                        if (categoryVars.length === 0) return null;
                        return (
                          <div key={category} className="mb-3">
                            <p className="text-xs font-semibold text-gray-700 mb-1">{category}</p>
                            <div className="space-y-1">
                              {categoryVars.map(variable => (
                                <button
                                  key={variable.name}
                                  type="button"
                                  onClick={() => insertVariable(variable.name, false)}
                                  className="w-full text-left px-2 py-1 text-xs bg-white hover:bg-blue-50 border border-gray-200 rounded transition-colors"
                                >
                                  <code className="text-blue-600">{'{{' + variable.name + '}}'}</code>
                                  <span className="text-gray-500 ml-1 text-[10px]">- {variable.label}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 justify-end">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowCreateModal(false);
                      setNewTemplate({
                        name: '',
                        description: '',
                        type: 'custom',
                        content_template: '',
                        required_fields: []
                      });
                    }}
                  >
                    Cancel
                  </Button>
                  <Button className="btn-snow" onClick={handleCreateTemplate}>
                    Create Template
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* View Template Modal */}
        {showViewModal && selectedTemplate && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-3xl max-h-[90vh] overflow-auto">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>{selectedTemplate.name}</span>
                  {!selectedTemplate.is_predefined && (
                    <Button 
                      size="sm" 
                      onClick={() => {
                        setShowViewModal(false);
                        handleEditTemplate(selectedTemplate);
                      }}
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <p className="text-gray-700">{selectedTemplate.description}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Type</label>
                  <p className="text-gray-700">{selectedTemplate.type}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Template Content</label>
                  <div className="bg-white p-6 rounded border border-gray-200 prose prose-sm max-w-none"
                    style={{ 
                      lineHeight: '1.8',
                      fontFamily: 'Georgia, serif'
                    }}
                    dangerouslySetInnerHTML={{ 
                      __html: selectedTemplate.content_template 
                    }}
                  />
                </div>

                {selectedTemplate.required_fields && selectedTemplate.required_fields.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Required Fields</label>
                    <div className="flex flex-wrap gap-2">
                      {selectedTemplate.required_fields.map((field, idx) => (
                        <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                          {field}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowViewModal(false);
                      setSelectedTemplate(null);
                    }}
                  >
                    Close
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Edit Template Modal */}
        {showEditModal && editingTemplate && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-6xl max-h-[90vh] overflow-auto">
              <CardHeader>
                <CardTitle>Edit Template</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Template Name *</label>
                  <Input
                    value={editingTemplate.name}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, name: e.target.value })}
                    placeholder="e.g., My Custom Contract"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <Input
                    value={editingTemplate.description}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, description: e.target.value })}
                    placeholder="Brief description of this template"
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  {/* Template Content - 2 columns */}
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1">Template Content *</label>
                    <Textarea
                      ref={editTextareaRef}
                      value={editingTemplate.content_template}
                      onChange={(e) => setEditingTemplate({ ...editingTemplate, content_template: e.target.value })}
                      rows={16}
                      placeholder="Enter your contract template here. Click variables on the right to insert them."
                      className="font-mono text-sm"
                    />
                  </div>

                  {/* Variables Sidebar - 1 column */}
                  <div className="col-span-1">
                    <label className="block text-sm font-medium mb-1">Available Variables</label>
                    <div className="border rounded-lg p-3 bg-gray-50 max-h-[400px] overflow-y-auto">
                      <p className="text-xs text-gray-600 mb-2">Click to insert:</p>
                      {['Project', 'Resource', 'Project Manager Company', 'Resource Manager Company', 'Terms', 'Financial', 'Location', 'Legal'].map(category => {
                        const categoryVars = AVAILABLE_VARIABLES.filter(v => v.category === category);
                        if (categoryVars.length === 0) return null;
                        return (
                          <div key={category} className="mb-3">
                            <p className="text-xs font-semibold text-gray-700 mb-1">{category}</p>
                            <div className="space-y-1">
                              {categoryVars.map(variable => (
                                <button
                                  key={variable.name}
                                  type="button"
                                  onClick={() => insertVariable(variable.name, true)}
                                  className="w-full text-left px-2 py-1 text-xs bg-white hover:bg-blue-50 border border-gray-200 rounded transition-colors"
                                >
                                  <code className="text-blue-600">{'{{' + variable.name + '}}'}</code>
                                  <span className="text-gray-500 ml-1 text-[10px]">- {variable.label}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 justify-end">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingTemplate(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button className="btn-snow" onClick={handleUpdateTemplate}>
                    Save Changes
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </>
  );
};

export default ContractTemplatesPage;
